import React, { FC,  useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { CloseCircleFilled, Loading3QuartersOutlined } from '@ant-design/icons';
import {
  DataCenterHeader,
  DataCenterDetailsWrap,
  MsText,
  DataCenterDescription,
  Link,
} from './style';
import { customProperties as vars } from '@resi-ui/design-system';
import { getClosestRegion } from '../../helpers/event-profile-onboarding';
import { LatencyDetails } from "../../constants";

export enum DATA_TESTID {
  LOADING_SPINNER_WRAP = 'loading-spinner-wrap',
  TIMED_OUT_HEADER = 'timed-out-header',
  BEST_LOCATION_HEADER = 'best-location-header',
}

type DataCenterLatencyProps = {
  onRegionFound: (bestLatency: LatencyDetails | null) => void;
  onTimeout?: () => void;
}

const DataCenterLatency: FC<DataCenterLatencyProps> = ({ onRegionFound, onTimeout }): JSX.Element => {
  const [bestLatency, setBestLatency] = useState<LatencyDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isTimedout, setTimedout] = useState<boolean>(false);

  useEffect(() => {
    (async (): Promise<void> => {
      const bestLatency = await getClosestRegion();

      setLoading(false);
      setBestLatency(bestLatency);

      if (bestLatency === null) {
        setTimedout(true);
        if (onTimeout) {
          onTimeout();
        }
      } else if (bestLatency.isPassing) {
        onRegionFound(bestLatency);
      }
    })();
  }, []);

  return (
      <>
        {loading && (
          <DataCenterDetailsWrap data-testid={DATA_TESTID.LOADING_SPINNER_WRAP}>
            <DataCenterHeader>Locating Data Center</DataCenterHeader>
            <Loading3QuartersOutlined spin style={{ color: vars.colorPrimary, fontSize: 14 }} />
          </DataCenterDetailsWrap>
        )}
        {!loading && bestLatency && (
          <>
            <DataCenterHeader data-testid={DATA_TESTID.BEST_LOCATION_HEADER}>
              {bestLatency.dataCenter.displayName} - <MsText passing={bestLatency.isPassing}>{bestLatency.duration}ms</MsText>
            </DataCenterHeader>
            {bestLatency.isPassing && (
              <DataCenterDescription passing={true}>
                This is configured based on your location. Please conduct this test at your streaming location
                if possible.
              </DataCenterDescription>
            )}
            {!bestLatency.isPassing && (
              <DataCenterDescription passing={false}>
                <CloseCircleFilled style={{ color: vars.colorError, fontSize: 8.75, marginRight: 3 }} />{' '}
                <span>
                            We failed to locate a data center with suitable performance. Please{' '}
                  <Link target="_blank" href="https://resi.io/support/">
                              contact support
                            </Link>{' '}
                  for further assistance
                          </span>
              </DataCenterDescription>
            )}
          </>
        )}
        {!loading && isTimedout && (
          <>
            <DataCenterDetailsWrap>
              <DataCenterHeader data-testid={DATA_TESTID.TIMED_OUT_HEADER} timedOut>
                Timeout Error
              </DataCenterHeader>
            </DataCenterDetailsWrap>
            <DataCenterDescription passing={false}>
              <CloseCircleFilled style={{ color: vars.colorError, fontSize: 8.75, marginRight: 3 }} />{' '}
              <span>
                      We failed to locate a data center with suitable performance. Please{' '}
                <Link target="_blank" href="https://resi.io/support/">
                        contact support
                      </Link>{' '}
                for further assistance
                    </span>
            </DataCenterDescription>
          </>
        )}
      </>
  );
};

DataCenterLatency.displayName = 'DataCenterLatency';

DataCenterLatency.propTypes = {
  onRegionFound: PropTypes.any.isRequired,
  onTimeout: PropTypes.any,
};

export default DataCenterLatency;
